<main class="bg-black text-white min-h-screen overflow-x-hidden font-montserrat">

  <!-- ===== HERO SECTION ===== -->
  <section class="relative min-h-[88vh] flex items-center pt-24 pb-16 overflow-hidden page-hero-gradient">
    <!-- Ambient glow -->
    <div class="absolute -top-48 -left-48 w-[600px] h-[600px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-varroc-accent/4 rounded-full blur-3xl"></div>
    <!-- Grid overlay -->
    <div class="absolute inset-0 opacity-[0.03]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <a routerLink="/" class="back-nav" data-aos="fade-down">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </a>
          <h1 class="text-5xl md:text-6xl font-black leading-tight mb-6" data-aos="fade-up" data-aos-delay="100">
            The Intelligence<br>
            <span class="text-varroc-accent">Beneath Modern</span><br>
            Mobility
          </h1>
          <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-xl" data-aos="fade-up" data-aos-delay="200">
            A unified, cloud-native platform built for the demands of connected vehicle ecosystems — from OEM telematics to consumer mobility intelligence.
          </p>
          <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
            <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all duration-300">
              Book a Demo
            </a>
           
          </div>
        </div>

        <!-- Stats grid -->
        <div class="grid grid-cols-2 gap-4" data-aos="fade-left" data-aos-delay="200">
          <div class="feature-card text-center">
            <div class="text-4xl font-black text-varroc-accent mb-2">100K+</div>
            <div class="text-white font-semibold text-sm">Events / Minute</div>
            <div class="text-varroc-text-gray text-xs mt-1">Real-time processing</div>
          </div>
          <div class="feature-card text-center">
            <div class="text-4xl font-black text-varroc-accent mb-2">&lt;100ms</div>
            <div class="text-white font-semibold text-sm">Latency</div>
            <div class="text-varroc-text-gray text-xs mt-1">Edge to cloud</div>
          </div>
          <div class="feature-card text-center">
            <div class="text-4xl font-black text-varroc-accent mb-2">500K+</div>
            <div class="text-white font-semibold text-sm">Vehicles Connected</div>
            <div class="text-varroc-text-gray text-xs mt-1">Across fleets globally</div>
          </div>
          <div class="feature-card text-center">
            <div class="text-4xl font-black text-varroc-accent mb-2">99.9%</div>
            <div class="text-white font-semibold text-sm">Uptime SLA</div>
            <div class="text-varroc-text-gray text-xs mt-1">Enterprise-grade reliability</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ===== STICKY HORIZONTAL NAV ===== -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto scrollbar-hide">
        <a *ngFor="let s of sections"
           (click)="scrollTo(s.id)"
           [class.active]="activeSection === s.id"
           class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i>
          {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <div class="max-w-7xl mx-auto px-6 py-20 space-y-32">

    <!-- ===== UNIFIED TELEMATICS PLATFORM ===== -->
    <section id="telematics" class="section-anchor">
      <div class="mb-16" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Core Infrastructure</span>
        <h2 class="text-4xl md:text-5xl font-black mt-3 mb-4">Unified Telematics Platform</h2>
        <p class="text-varroc-text-gray text-lg max-w-2xl mr-auto">One platform to connect, manage, and extract value from every vehicle across your fleet — regardless of make, model, or hardware.</p>
      </div>

      <!-- Feature cards -->
      <div class="grid md:grid-cols-3 gap-6 mb-12">
        <div class="feature-card" data-aos="fade-up" data-aos-delay="0">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-network-chart icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">Universal Connectivity</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Supports OBD-II, CAN Bus, cellular, Bluetooth and proprietary protocols across all vehicle types.</p>
        </div>
        <div class="feature-card" data-aos="fade-up" data-aos-delay="100">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-cloud icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">Edge-to-Cloud Pipeline</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Bidirectional data flow from edge hardware to cloud analytics with real-time streaming and buffering.</p>
        </div>
        <div class="feature-card" data-aos="fade-up" data-aos-delay="200">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-transfer icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">OTA Management</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Over-the-air firmware and configuration updates with rollback protection and staged deployment.</p>
        </div>
        <div class="feature-card" data-aos="fade-up" data-aos-delay="300">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-sitemap icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">Device Fleet Management</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Manage thousands of connected devices from a single dashboard with health monitoring and alerts.</p>
        </div>
        <div class="feature-card" data-aos="fade-up" data-aos-delay="400">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-map-alt icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">Real-time Location</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Sub-second GPS tracking with geofencing, route replay, and location intelligence APIs.</p>
        </div>
        <div class="feature-card" data-aos="fade-up" data-aos-delay="500">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx bx-broadcast icon-gradient text-2xl'></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">Multi-Protocol Support</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">MQTT, HTTPS, WebSocket and proprietary protocols for any device or integration requirement.</p>
        </div>
      </div>

      <!-- Architecture banner -->
      <div class="bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6" data-aos="fade-up">
        <div class="flex-1">
          <h3 class="text-white font-bold text-xl mb-2">Platform Architecture Overview</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">Our microservices architecture scales horizontally to handle 100M+ daily events with zero downtime deployments.</p>
        </div>
        <a routerLink="/contact-us" class="flex-shrink-0 bg-varroc-accent text-white font-bold px-6 py-3 rounded-md hover:bg-varroc-accent-hover transition-all">
          Get Architecture Deck
        </a>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- ===== MODULAR SDK & APIs ===== -->
    <section id="sdk" class="section-anchor">
      <div class="mb-16" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Developer First</span>
        <h2 class="text-4xl md:text-5xl font-black mt-3 mb-4">Modular SDK & APIs</h2>
        <p class="text-varroc-text-gray text-lg max-w-2xl mr-auto">Pick only what you need. Our modular approach lets you integrate telematics capabilities into any existing stack in days, not months.</p>
      </div>

      <div class="grid md:grid-cols-2 gap-8 mb-12">
        <!-- SDK Modules -->
        <div data-aos="fade-right">
          <h3 class="text-white font-bold text-xl mb-6 flex items-center gap-2">
            <i class='bx bx-code-curly icon-gradient text-2xl'></i> SDK Modules
          </h3>
          <div class="space-y-4">
            <div *ngFor="let sdk of [
              {name:'Vehicle Data SDK', desc:'Access raw and processed vehicle telemetry in any mobile or web app'},
              {name:'Location & Trip SDK', desc:'Trip detection, route tracking, and geofencing in a single package'},
              {name:'Driver Score SDK', desc:'Real-time driver behaviour scoring on-device or server-side'},
              {name:'Notification SDK', desc:'Push contextual alerts and safety messages based on driving events'}
            ]" class="flex items-start gap-4 p-4 rounded-xl border border-varroc-border hover:border-varroc-accent/30 transition-all">
              <div class="w-2 h-2 rounded-full bg-varroc-accent mt-2 flex-shrink-0"></div>
              <div>
                <div class="text-white font-semibold text-sm">{{ sdk.name }}</div>
                <div class="text-varroc-text-gray text-xs mt-0.5">{{ sdk.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- API Endpoints -->
        <div data-aos="fade-left">
          <h3 class="text-white font-bold text-xl mb-6 flex items-center gap-2">
            <i class='bx bx-terminal icon-gradient text-2xl'></i> REST & Stream APIs
          </h3>
          <div class="bg-varroc-dark border border-varroc-border rounded-xl overflow-hidden font-mono text-sm">
            <div class="bg-black/60 px-4 py-2 border-b border-varroc-border flex items-center gap-2">
              <div class="w-3 h-3 rounded-full bg-red-500/70"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500/70"></div>
              <div class="w-3 h-3 rounded-full bg-green-500/70"></div>
              <span class="text-varroc-text-gray text-xs ml-2">api.varrocconnect.com</span>
            </div>
            <div class="p-5 space-y-3 text-xs">
              <div><span class="text-green-400">GET</span>  <span class="text-varroc-accent">/v2/vehicles/&#123;id&#125;/telemetry</span></div>
              <div><span class="text-blue-400">POST</span> <span class="text-varroc-accent">/v2/trips/analyze</span></div>
              <div><span class="text-green-400">GET</span>  <span class="text-varroc-accent">/v2/driver/&#123;id&#125;/score</span></div>
              <div><span class="text-yellow-400">WS</span>   <span class="text-varroc-accent">/v2/stream/live-location</span></div>
              <div><span class="text-green-400">GET</span>  <span class="text-varroc-accent">/v2/fleet/health</span></div>
              <div><span class="text-blue-400">POST</span> <span class="text-varroc-accent">/v2/events/ingest</span></div>
            </div>
          </div>
          <div class="mt-4 grid grid-cols-3 gap-3 text-center">
            <div class="bg-varroc-dark border border-varroc-border rounded-lg p-3">
              <div class="text-varroc-accent font-black text-lg">50+</div>
              <div class="text-varroc-text-gray text-xs">REST Endpoints</div>
            </div>
            <div class="bg-varroc-dark border border-varroc-border rounded-lg p-3">
              <div class="text-varroc-accent font-black text-lg">6</div>
              <div class="text-varroc-text-gray text-xs">Stream APIs</div>
            </div>
            <div class="bg-varroc-dark border border-varroc-border rounded-lg p-3">
              <div class="text-varroc-accent font-black text-lg">99%</div>
              <div class="text-varroc-text-gray text-xs">Uptime SLA</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- ===== MILES ENGINE ===== -->
    <section id="miles" class="section-anchor">
      <div class="mb-16" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Proprietary AI Core</span>
        <h2 class="text-4xl md:text-5xl font-black mt-3 mb-4">Miles Engine</h2>
        <p class="text-varroc-text-gray text-lg max-w-2xl mr-auto">The AI brain of the Varroc Connect platform — transforming raw telematics events into actionable mobility intelligence.</p>
      </div>

      <div class="grid md:grid-cols-3 gap-6">
        <div class="feature-card md:col-span-1" data-aos="fade-up">
          <div class="w-14 h-14 bg-varroc-accent/15 rounded-2xl flex items-center justify-center mb-6">
            <i class='bx bx-brain icon-gradient text-3xl'></i>
          </div>
          <h3 class="text-white font-bold text-xl mb-3">ML-Powered Trip Intelligence</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed mb-4">Automatically detects trip start/end, classifies road types, identifies aggressive events, and computes risk exposure per mile.</p>
          <ul class="space-y-2">
            <li *ngFor="let f of ['Trip segmentation','Road classification','Event detection','Risk scoring']" class="flex items-center gap-2 text-sm text-varroc-text-gray">
              <i class='bx bx-check-circle icon-gradient'></i> {{ f }}
            </li>
          </ul>
        </div>
        <div class="md:col-span-2 grid grid-cols-2 gap-6">
          <div class="feature-card" data-aos="fade-up" data-aos-delay="100">
            <i class='bx bx-car icon-gradient text-2xl mb-3'></i>
            <h4 class="text-white font-bold mb-2">Vehicle Health Predictions</h4>
            <p class="text-varroc-text-gray text-xs leading-relaxed">Predictive maintenance alerts before failures occur using ML models trained on 500K+ connected vehicles.</p>
          </div>
          <div class="feature-card" data-aos="fade-up" data-aos-delay="150">
            <i class='bx bx-map icon-gradient text-2xl mb-3'></i>
            <h4 class="text-white font-bold mb-2">Context-Aware Routing</h4>
            <p class="text-varroc-text-gray text-xs leading-relaxed">Fuel-optimal, time-aware route suggestions enriched with real-world traffic patterns from the fleet network.</p>
          </div>
          <div class="feature-card" data-aos="fade-up" data-aos-delay="200">
            <i class='bx bx-user icon-gradient text-2xl mb-3'></i>
            <h4 class="text-white font-bold mb-2">Driver Profiling</h4>
            <p class="text-varroc-text-gray text-xs leading-relaxed">Individual driver fingerprinting, behaviour trends, and comparative scoring against fleet baselines.</p>
          </div>
          <div class="feature-card" data-aos="fade-up" data-aos-delay="250">
            <i class='bx bx-trending-up icon-gradient text-2xl mb-3'></i>
            <h4 class="text-white font-bold mb-2">Fuel & EV Insights</h4>
            <p class="text-varroc-text-gray text-xs leading-relaxed">Consumption analytics, idling detection, and battery charge optimization for mixed ICE and EV fleets.</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- ===== ANALYTICS & INTELLIGENCE ===== -->
    <section id="analytics" class="section-anchor">
      <div class="mb-16" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Data Platform</span>
        <h2 class="text-4xl md:text-5xl font-black mt-3 mb-4">Analytics & Intelligence</h2>
        <p class="text-varroc-text-gray text-lg max-w-2xl mr-auto">Turn every mile of vehicle data into competitive advantage — with dashboards, custom reports, and embeddable analytics.</p>
      </div>

      <!-- Dashboard showcase cards -->
      <div class="grid md:grid-cols-4 gap-4 mb-12">
        <div *ngFor="let card of [
          {icon:'bx-line-chart', title:'Fleet Analytics', desc:'Real-time fleet KPIs with drill-down capability'},
          {icon:'bx-user-check', title:'Driver Scorecards', desc:'Individual and cohort scoring with trend lines'},
          {icon:'bx-map-alt', title:'Mobility Heatmaps', desc:'Origin-destination analysis and route density maps'},
          {icon:'bx-bell', title:'Alert Intelligence', desc:'Smart alerting with severity classification and escalation'}
        ]" class="feature-card text-center" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="($index * 100).toString()">
          <i class='bx icon-gradient text-3xl mb-3' [class]="card.icon"></i>
          <h4 class="text-white font-bold text-sm mb-2">{{ card.title }}</h4>
          <p class="text-varroc-text-gray text-xs leading-relaxed">{{ card.desc }}</p>
        </div>
      </div>

      <!-- Custom reports CTA -->
      <div class="bg-varroc-dark border border-varroc-border rounded-2xl p-8 grid md:grid-cols-3 gap-8 items-center" data-aos="fade-up">
        <div class="md:col-span-2">
          <h3 class="text-white font-bold text-xl mb-2">Need custom reporting?</h3>
          <p class="text-varroc-text-gray text-sm">Our analytics team can build bespoke dashboards and automated reports tailored to your business metrics and stakeholder requirements.</p>
        </div>
        <a routerLink="/contact-us" class="text-center bg-varroc-accent text-white font-bold px-6 py-3 rounded-md hover:bg-varroc-accent-hover transition-all">
          Talk to Our Team
        </a>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- ===== SECURITY & COMPLIANCE ===== -->
    <section id="security" class="section-anchor">
      <div class="mb-16" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Trust & Safety</span>
        <h2 class="text-4xl md:text-5xl font-black mt-3 mb-4">Security & Compliance</h2>
        <p class="text-varroc-text-gray text-lg max-w-2xl mr-auto">Enterprise-grade security baked into every layer of the platform — from device authentication to data sovereignty.</p>
      </div>

      <div class="grid md:grid-cols-2 gap-8">
        <div class="space-y-4" data-aos="fade-right">
          <div *ngFor="let item of [
            {icon:'bx-lock-alt', title:'End-to-End Encryption', desc:'AES-256 encryption in transit and at rest across all data paths.'},
            {icon:'bx-id-card', title:'Device Identity & Auth', desc:'PKI-based certificate management for every connected device.'},
            {icon:'bx-shield-quarter', title:'Role-Based Access Control', desc:'Granular RBAC with audit trails and session management.'},
            {icon:'bx-world', title:'Data Residency', desc:'Store data in your preferred region — India, EU, or US.'}
          ]" class="flex items-start gap-4 p-5 rounded-xl border border-varroc-border hover:border-varroc-accent/30 transition-all">
            <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <i class='bx icon-gradient text-xl' [class]="item.icon"></i>
            </div>
            <div>
              <div class="text-white font-semibold text-sm mb-1">{{ item.title }}</div>
              <div class="text-varroc-text-gray text-xs">{{ item.desc }}</div>
            </div>
          </div>
        </div>

        <div class="space-y-4" data-aos="fade-left">
          <h3 class="text-white font-bold text-xl mb-4">Certifications & Standards</h3>
          <div class="grid grid-cols-2 gap-4">
            <div *ngFor="let cert of ['ISO 27001', 'SOC 2 Type II', 'GDPR Ready', 'CERT-In Compliant', 'DISHA Framework', 'AIS-140 Compliant']" class="bg-varroc-dark border border-varroc-border rounded-xl p-4 text-center hover:border-varroc-accent/30 transition-all">
              <i class='bx bx-badge-check icon-gradient text-2xl block mb-2'></i>
              <span class="text-white font-semibold text-sm">{{ cert }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="text-center py-16 bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-3xl" data-aos="fade-up">
      <h2 class="text-4xl font-black mb-4">Ready to Build on the Platform?</h2>
      <p class="text-varroc-text-gray text-lg mb-8 max-w-xl mx-auto">Whether you're an OEM, fleet operator, or insurtech — our platform has the capabilities to power your vision.</p>
      <div class="flex flex-wrap gap-4 justify-center">
        <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-10 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all text-lg">
          Get Started Today
        </a>
        <a routerLink="/products" class="border border-varroc-accent/40 font-semibold px-10 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all text-lg">
          View Products
        </a>
      </div>
    </section>

  </div>
</main>
